// ---------------------------------------------------------------------------
// Nav Mesh — Thin wrapper around @recast-navigation.
// Generates a navigation mesh directly from Three.js scene geometry using
// industry-standard Recast/Detour (same as Unity/Unreal).
// ---------------------------------------------------------------------------

import * as THREE from 'three';
import { init as initRecast, NavMesh, NavMeshQuery, exportNavMesh, importNavMesh } from '@recast-navigation/core';
import { generateSoloNavMesh } from '@recast-navigation/generators';
import { getPositionsAndIndices } from '@recast-navigation/three';
import { EFFECT_LAYER } from '../renderer/enhanced-effects';
import { NavMeshHelper } from '@recast-navigation/three';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let navMesh: NavMesh | null = null;
let navMeshQuery: NavMeshQuery | null = null;
let debugHelper: NavMeshHelper | null = null;
let recastInitialized = false;

// ---------------------------------------------------------------------------
// Recast parameters (tuned for FPS player movement)
// ---------------------------------------------------------------------------

const FPS_NAV_PARAMS = {
  cs: 0.3,
  ch: 0.2,
  walkableSlopeAngle: 45,
  walkableHeight: 5,
  walkableClimb: 2,
  walkableRadius: 2,
  minRegionArea: 8,
  mergeRegionArea: 20,
  maxEdgeLen: 12,
  maxSimplificationError: 1.3,
  maxVertsPerPoly: 6,
  detailSampleDist: 6,
  detailSampleMaxError: 1,
};

// ---------------------------------------------------------------------------
// Initialization
// ---------------------------------------------------------------------------

async function ensureRecastInit(): Promise<void> {
  if (recastInitialized) return;
  await initRecast();
  recastInitialized = true;
}

// ---------------------------------------------------------------------------
// Nav mesh generation
// ---------------------------------------------------------------------------

/**
 * Generate a navigation mesh from the scene geometry.
 */
export async function generateNavMeshFromScene(scene: THREE.Scene): Promise<boolean> {
  await ensureRecastInit();

  // Collect walkable meshes
  const meshes: THREE.Mesh[] = [];
  scene.traverse((obj) => {
    if (!(obj instanceof THREE.Mesh)) return;
    if (obj.userData.isEffect) return;
    if (obj.userData.batchedAway) return;
    if (!obj.visible) return;

    const mat = obj.material as THREE.Material;
    if (mat.transparent && (mat as THREE.MeshStandardMaterial).opacity < 0.5) return;

    meshes.push(obj);
  });

  if (meshes.length === 0) {
    console.warn('NavMesh: No walkable meshes found');
    return false;
  }

  // Extract positions and indices from Three.js meshes
  const [positions, indices] = getPositionsAndIndices(meshes);

  // Generate solo nav mesh
  const result = generateSoloNavMesh(positions, indices, FPS_NAV_PARAMS);

  if (!result.success || !result.navMesh) {
    console.error('NavMesh generation failed');
    // Clear stale state so consumers don't use outdated nav mesh
    navMesh = null;
    navMeshQuery = null;
    return false;
  }

  navMesh = result.navMesh;
  navMeshQuery = new NavMeshQuery(navMesh);
  return true;
}

// ---------------------------------------------------------------------------
// Pathfinding API
// ---------------------------------------------------------------------------

/**
 * Find a path between two world positions.
 */
export function findPath(
  start: THREE.Vector3,
  end: THREE.Vector3,
): THREE.Vector3[] | null {
  if (!navMeshQuery) return null;

  const { path } = navMeshQuery.computePath(
    { x: start.x, y: start.y, z: start.z },
    { x: end.x, y: end.y, z: end.z },
  );

  if (!path || path.length === 0) return null;

  return path.map((p: { x: number; y: number; z: number }) =>
    new THREE.Vector3(p.x, p.y, p.z),
  );
}

/**
 * Find the nearest point on the nav mesh to a given position.
 */
export function findNearestPoint(
  position: THREE.Vector3,
  halfExtents = { x: 2, y: 4, z: 2 },
): THREE.Vector3 | null {
  if (!navMeshQuery) return null;

  const { point } = navMeshQuery.findClosestPoint(
    { x: position.x, y: position.y, z: position.z },
    { halfExtents },
  );

  if (!point) return null;
  return new THREE.Vector3(point.x, point.y, point.z);
}

/**
 * Check if a position is on the nav mesh.
 */
export function isOnNavMesh(
  position: THREE.Vector3,
  tolerance = 0.5,
): boolean {
  const nearest = findNearestPoint(position);
  if (!nearest) return false;
  return nearest.distanceTo(position) < tolerance;
}

/**
 * Get a random point on the nav mesh.
 */
export function getRandomNavMeshPoint(): THREE.Vector3 | null {
  if (!navMeshQuery) return null;

  const result = navMeshQuery.findRandomPoint();
  if (!result.success) return null;
  const p = result.randomPoint;
  return new THREE.Vector3(p.x, p.y, p.z);
}

// ---------------------------------------------------------------------------
// Debug visualization
// ---------------------------------------------------------------------------

/**
 * Create or update the debug visualization for the nav mesh.
 */
export function getNavMeshDebugHelper(scene: THREE.Scene): NavMeshHelper | null {
  if (!navMesh) return null;

  if (debugHelper) {
    scene.remove(debugHelper);
  }

  debugHelper = new NavMeshHelper(navMesh, {
    navMeshMaterial: new THREE.MeshBasicMaterial({
      color: 0x00ff88,
      transparent: true,
      opacity: 0.3,
      wireframe: false,
      side: THREE.DoubleSide,
    }),
  });
  debugHelper.position.y = 0.05;
  debugHelper.layers.set(EFFECT_LAYER); // Debug only — keep off raycast layer

  scene.add(debugHelper);
  return debugHelper;
}

/**
 * Toggle debug visualization on/off.
 */
export function toggleNavMeshDebug(scene: THREE.Scene, visible: boolean): void {
  if (visible && !debugHelper) {
    getNavMeshDebugHelper(scene);
  }
  if (debugHelper) {
    debugHelper.visible = visible;
  }
}

// ---------------------------------------------------------------------------
// Serialization
// ---------------------------------------------------------------------------

export function serializeNavMesh(): Uint8Array | null {
  if (!navMesh) return null;
  return exportNavMesh(navMesh);
}

export async function deserializeNavMesh(data: Uint8Array): Promise<boolean> {
  await ensureRecastInit();

  const result = importNavMesh(data);
  if (!result.navMesh) return false;

  navMesh = result.navMesh;
  navMeshQuery = new NavMeshQuery(navMesh);
  return true;
}

// ---------------------------------------------------------------------------
// Cleanup
// ---------------------------------------------------------------------------

export function disposeNavMesh(scene?: THREE.Scene): void {
  if (debugHelper) {
    if (scene) scene.remove(debugHelper);
    // NavMeshHelper is a Group of meshes; dispose geometries and materials
    // to avoid leaking the BasicMaterial we allocated in getNavMeshDebugHelper.
    debugHelper.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        obj.geometry?.dispose();
        if (Array.isArray(obj.material)) {
          for (const m of obj.material) m.dispose();
        } else if (obj.material) {
          obj.material.dispose();
        }
      }
    });
    debugHelper = null;
  }

  if (navMesh) {
    navMesh.destroy();
    navMesh = null;
  }
  if (navMeshQuery) {
    navMeshQuery.destroy();
    navMeshQuery = null;
  }
}

export function hasNavMesh(): boolean {
  return navMesh !== null;
}
